import {
  Box,
  Button,
  FlexBox,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { HookFormText } from 'lib-shared-hook-form';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import ComposeWindowPortal from 'assets/components/shared/composeEmail/components/ComposeWindowPortal';
import { AttachmentsFieldList } from 'assets/components/shared/util/richTextField/components/components/attachmentsFieldList/attachmentsFieldList';
import {
  HookFormCategorizedTagsSelect,
  HookFormEmailSelect,
  RichTextFieldRef,
} from 'components/hookForm';
// eslint-disable-next-line no-restricted-imports
import { ToggleBox, ToggleBoxBody, ToggleBoxHead } from 'fable/toggleBox';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { createAppModal } from 'utils/modal';
import { useServerStorage } from 'utils/useServerStorage/useServerStorage';

import { ComposeCustomAttributesToggle } from '../composeCustomAttributes';

import { ComposeEmailFieldCsatLanguage } from './components/composeEmailFieldCsatLanguage';
import { ComposeEmailFieldEmailContact } from './components/composeEmailFieldEmailContact';
import { ComposeEmailFieldRichText } from './components/composeEmailFieldRichText';
import { ComposeEmailLinkedBanner } from './components/composeEmailLinkedBanner';
import { ComposeEmailQueueSection } from './components/composeEmailQueueSection';
import styles from './composeEmailModal.module.scss';
import type { FormValues } from './composeEmailModal.types';
import { convertEmailDraftV2ToFormValues } from './composeEmailModal.utils';
import { useAttachments } from './hooks/useAttachments';
import { useCcBcc } from './hooks/useCcBcc';
import { useComposeEmailSubmit } from './hooks/useComposeEmailSubmit';
import { useEmailDraft } from './hooks/useEmailDraft';
import { useLinkedConversationData } from './hooks/useLinkedConversationData';

const formId = 'compose-email';

export const ComposeEmailModal = createAppModal(
  { type: 'app:composeEmail', featureFlag: 'email-compose-v2' },
  (props): JSX.Element => {
    const {
      isOpen,
      hide,
      modal: { arg: { draft, initialValues } = {} } = {},
    } = props;

    const bodyEditorRef = useRef<RichTextFieldRef>(null);

    const flags = useFeatureFlag(['custom-attributes-in-compose-email']);

    const parentConversationInitial =
      initialValues?.parentConversation ?? draft?.parentConversation ?? null;
    const linkTypeInitial = initialValues?.linkType ?? draft?.linkType ?? null;
    const { data: linkedData, isLoading: linkedDataLoading } =
      useLinkedConversationData(parentConversationInitial);

    const { data: userStorage } = useServerStorage('user');
    const lastUsedEmail = userStorage?.global?.lastUsedEmailIntegration;

    const form = useForm<FormValues>({
      defaultValues: {
        toEmail: [],
        fromEmail: lastUsedEmail ? [lastUsedEmail] : null,
        cc: [],
        bcc: [],
        subject: '',
        attachments_v2: [],
        body: '',
        offerInQueue: false,
        queueId: null,
        tags: [],
        offer: false,
        includeInternalNote: false,
        internalNote: '',
        linkType: null,
        parentConversation: null,
        customAttributes: {},
        customAttributesOpen: false,
        satisfactionLanguage: null,
        cancelledSatisfaction: false,
        ...(draft ? convertEmailDraftV2ToFormValues(draft) : {}),
        ...(linkTypeInitial === 'email_forward' && linkedData
          ? {
              body: linkedData.body,
              subject: linkedData.subject,
              attachments_v2: linkedData.attachments,
            }
          : {}),
        ...(initialValues ?? {}),
      },
      mode: 'all',
    });
    const {
      control,
      formState: { isValid, isSubmitting },
      getValues,
      handleSubmit,
      watch,
      setValue,
    } = form;

    const parentConversation = watch('parentConversation');
    const linkType = watch('linkType');
    const fromEmail = watch('fromEmail');
    const fromEmailValue = fromEmail?.[0] ?? null;
    const queue = watch('queueId');
    const showInternalNote = watch('includeInternalNote');
    const offerInQueue = watch('offerInQueue');

    const { ccEnabled, bccEnabled, ccOnClick, bccOnClick } = useCcBcc({
      initialCcEnabled: watch('cc').length > 0,
      initialBccEnabled: watch('bcc').length > 0,
      setValue,
    });

    const { handleSaveDraft, deleteDraft } = useEmailDraft({
      draft,
      getValues,
      watch,
      hide,
    });

    const { onSubmit, sendFailed } = useComposeEmailSubmit({
      deleteDraft,
      hide,
    });

    const { attachmentFieldsAdapter, handleAttachmentAdded } = useAttachments({
      control,
    });

    const handleChangeSubject = useCallback(
      (subject: string) => {
        setValue('subject', subject);
      },
      [setValue],
    );

    useEffect(() => {
      if (!queue?.length && offerInQueue) {
        setValue('offerInQueue', false);
      }
    }, [queue, offerInQueue, setValue]);

    const handleCopyPaste = useCallback(() => {
      if (!linkedData) return;

      const currentBody = getValues('body');
      const newBody = currentBody
        ? `${currentBody}\n\n${linkedData.body}`
        : linkedData.body;
      setValue('body', newBody);
      bodyEditorRef.current?.setText(newBody);

      if (!getValues('subject')) {
        setValue('subject', linkedData.subject);
      }

      const existing = getValues('attachments_v2');
      const newAttachments = linkedData.attachments.filter(
        (a) => !existing.some((e) => e.url === a.url),
      );
      setValue('attachments_v2', [...existing, ...newAttachments]);
    }, [linkedData, getValues, setValue]);

    const [showWindowPortal, setShowWindowPortal] = useState(false);
    const [popoutWindow, setPopoutWindow] = useState<Window | undefined>(
      undefined,
    );

    const toggleWindowPortal = useCallback(
      () => setShowWindowPortal((v) => !v),
      [],
    );

    const closeWindowPortal = useCallback(() => {
      setShowWindowPortal(false);
      setPopoutWindow(undefined);
    }, []);

    useEffect(() => {
      if (!isOpen && showWindowPortal) {
        setShowWindowPortal(false);
        setPopoutWindow(undefined);
      }
    }, [isOpen, showWindowPortal]);

    const modalContent = (
      <>
        <ModalHead
          title="Compose new email"
          onClose={hide}
          secondaryButton={{
            onClick: toggleWindowPortal,
            tooltipContent: showWindowPortal
              ? 'Show in main window'
              : 'Open in new window',
            icon: showWindowPortal ? 'compress' : 'external-link',
          }}
        />

        <ModalBody>
          <form onSubmit={handleSubmit(onSubmit)} id={formId}>
            <FormGrid>
              {parentConversation && (
                <FormField span={6}>
                  <ComposeEmailLinkedBanner
                    linkType={linkType}
                    parentConversation={parentConversation}
                    linkedDataLoading={linkedDataLoading}
                    onCopyPaste={handleCopyPaste}
                  />
                </FormField>
              )}

              <FormField span={6}>
                <ComposeEmailFieldEmailContact
                  name="toEmail"
                  label="To"
                  required
                  componentProps={{
                    suffix: {
                      type: 'custom',
                      noWrap: true,
                      value: (
                        <>
                          <Button
                            variant={ccEnabled ? 'primary' : 'tertiary'}
                            className={styles.suffixButton}
                            onClick={ccOnClick}
                          >
                            CC
                          </Button>
                          <Button
                            variant={bccEnabled ? 'primary' : 'tertiary'}
                            className={styles.suffixButton}
                            onClick={bccOnClick}
                          >
                            BCC
                          </Button>
                        </>
                      ),
                    },
                  }}
                />
              </FormField>

              {(ccEnabled || bccEnabled) && (
                <FormField>
                  <FlexBox padding="250 400" className={styles.optionalBox}>
                    <FormGrid>
                      {ccEnabled && (
                        <FormField span={6}>
                          <ComposeEmailFieldEmailContact
                            name="cc"
                            label="CC"
                            multiple
                          />
                        </FormField>
                      )}
                      {bccEnabled && (
                        <FormField span={6}>
                          <ComposeEmailFieldEmailContact
                            name="bcc"
                            label="BCC"
                            multiple
                          />
                        </FormField>
                      )}
                    </FormGrid>
                  </FlexBox>
                </FormField>
              )}

              <FormField>
                <HookFormEmailSelect
                  control={control}
                  name="fromEmail"
                  label="From"
                />
              </FormField>

              <FormField>
                <HookFormText
                  name="subject"
                  control={control}
                  label="Subject"
                  rules={{ required: 'Subject is required' }}
                />
              </FormField>

              <FormField>
                <ComposeEmailFieldRichText
                  name="body"
                  label="Message"
                  placeholder="Type a message"
                  required
                  editorRef={bodyEditorRef}
                  onChangeSubject={handleChangeSubject}
                  customWindow={popoutWindow}
                  attachmentsFields={
                    <AttachmentsFieldList fields={attachmentFieldsAdapter} />
                  }
                  onAttachmentAdded={handleAttachmentAdded}
                />
              </FormField>

              <FormField>
                <Box margin="0 0 300" width="100%">
                  <ToggleBox
                    className={styles.optionalBox}
                    isOpen={showInternalNote}
                    headComponent={ToggleBoxHead}
                    title="Internal note"
                    helpIcon={undefined}
                    input={{
                      id: 'internal-note-toggle-box',
                      name: 'internal-note-toggle-box',
                      value: 'internal-note-toggle-box',
                    }}
                    onChange={(open) => setValue('includeInternalNote', open)}
                  >
                    <ToggleBoxBody>
                      {showInternalNote && (
                        <FormField>
                          <ComposeEmailFieldRichText
                            name="internalNote"
                            label="Internal note"
                            placeholder="Type an internal note"
                            customWindow={popoutWindow}
                          />
                        </FormField>
                      )}
                    </ToggleBoxBody>
                  </ToggleBox>
                </Box>
              </FormField>

              {flags.customAttributesInComposeEmail && (
                <FormField span={6}>
                  <ComposeCustomAttributesToggle
                    value={watch('customAttributes')}
                    onChange={(values) => setValue('customAttributes', values)}
                    queueId={watch('queueId')?.[0]}
                    isOpen={watch('customAttributesOpen')}
                    headComponent={ToggleBoxHead}
                  />
                </FormField>
              )}

              <FormField span={linkType === 'follow_up' ? 3 : 6}>
                <HookFormCategorizedTagsSelect
                  control={control}
                  label="Tags"
                  name="tags"
                  componentProps={{ icon: 'tag' }}
                />
              </FormField>

              {linkType === 'follow_up' && (
                <FormField span={3}>
                  <ComposeEmailFieldCsatLanguage
                    control={control}
                    fromEmailValue={fromEmailValue}
                  />
                </FormField>
              )}

              <ComposeEmailQueueSection control={control} queue={queue} />
            </FormGrid>
          </form>
        </ModalBody>

        <ModalFoot
          cancelName="Cancel"
          cancelOnClick={hide}
          confirmSecondaryName="Save draft"
          confirmSecondaryOnClick={handleSaveDraft}
          confirmName={sendFailed ? 'Failed – try again' : 'Send'}
          confirmOnClick={() => undefined}
          confirmProps={{
            type: 'submit',
            disabled: !isValid || isSubmitting,
            isLoading: isSubmitting,
            loadingIndicator: 'Sending...',
            form: formId,
            variant: sendFailed ? 'danger' : 'primary',
          }}
        />
      </>
    );

    return (
      <FormProvider {...form}>
        {showWindowPortal ? (
          <ComposeWindowPortal
            title="Compose email"
            copyStyles
            center="parent"
            name="_modal"
            url=""
            features={{
              width: 900,
              height: 710,
              directories: false,
              titlebar: false,
              toolbar: false,
              location: false,
              status: false,
              menubar: false,
              nodeIntegration: false,
              nativeWindowOpen: true,
            }}
            onUnload={closeWindowPortal}
            onBlock={closeWindowPortal}
            onOpen={(win: Window) => setPopoutWindow(win)}
          >
            <div className={styles.popout}>{modalContent}</div>
          </ComposeWindowPortal>
        ) : (
          <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
            {modalContent}
          </Modal>
        )}
      </FormProvider>
    );
  },
);
